import * as AceEditor from 'brace'
import Question from '../models/Question'
import QuestionType from './QuestionType'
import { ISkill } from '../../../types'
import Skill from '../models/Skill'

interface IAutoComplete {
  name: string
  snippet?: string
  value?: string
  meta: string
  score?: number
}
interface ICompletionCallback {
  (error: any, worldList: IAutoComplete[]): void
}
interface IPosition {
  column: number
  row: number
}

export default class AutoCompleter {
  readonly identifierRegexps = [/[\$\+\=\-\~(\s\s-\s)]/]
  private getSkills: () => Skill[]
  private getQuestion: () => Question
  private getQuestionTypes: () => QuestionType[]

  constructor(getQuestion: () => Question, getQuestionTypes: () => QuestionType[], getSkills: () => Skill[]) {
    this.getSkills = getSkills
    this.getQuestion = getQuestion
    this.getQuestionTypes = getQuestionTypes
  }

  public completer() {
    return {
      identifierRegexps: this.identifierRegexps,
      getCompletions: this.getCompletions.bind(this)
    }
  }

  public getCompletions(
    editor: AceEditor.Editor,
    session: AceEditor.IEditSession,
    pos: IPosition,
    prefix: string,
    callback: ICompletionCallback
  ) {
    if (this.suggestDivider(prefix, session, pos, callback)) return
    if (this.suggestQuestion(prefix, session, pos, callback)) return
    if (this.suggestSkill(prefix, session, pos, callback)) return
    if (this.suggestSkillItem(prefix, session, pos, callback)) return
  }

  private suggestDivider(
    prefix: string,
    session: AceEditor.IEditSession,
    pos: IPosition,
    callback: ICompletionCallback
  ): boolean {
    if (pos.column !== 1) return false

    const suggests = []
    if (prefix.match(/^={1,2}$/)) {
      suggests.push({ score: 1, meta: 'hard divider', name: 'hard divider', value: '===' })
    } else if (prefix.match(/^-{1,2}$/)) {
      suggests.push({ score: 1, meta: 'soft divider', name: 'soft divider', value: '---' })
    } else if (prefix.match(/^~{1,2}$/) && !this.inExplanation(session, pos)) {
      suggests.push({ score: 1, meta: 'explanation', name: 'explanation', value: '~~~' })
    } else {
      return false
    }

    callback(null, suggests)
    return true
  }

  private suggestQuestion(
    prefix: string,
    session: AceEditor.IEditSession,
    pos: IPosition,
    callback: ICompletionCallback
  ): boolean {
    if (prefix != '$' || this.inExplanation(session, pos)) return false

    let completionValues
    const currentQuestion = this.getQuestion()
    if (pos.column !== 1 && currentQuestion.fieldType !== 'textfield') return false
    if (currentQuestion.type) {
      completionValues = this.suggestionForQuestion(currentQuestion)
    } else {
      completionValues = this.suggestionForAllQuestion(currentQuestion)
    }

    callback(
      null,
      completionValues.map((suggest) => Object.assign({ score: 1 }, suggest))
    )
    return true
  }

  private suggestSkill(
    prefix: string,
    session: AceEditor.IEditSession,
    pos: IPosition,
    callback: ICompletionCallback
  ): boolean {
    if (!this.inExplanation(session, pos)) return false
    if (this.suggestAllSkill(prefix, session, pos, callback)) return true
    if (this.suggestInputingSkill(prefix, session, pos, callback)) return true
    return false
  }

  private suggestAllSkill(
    prefix: string,
    session: AceEditor.IEditSession,
    pos: IPosition,
    callback: ICompletionCallback
  ): boolean {
    if (prefix != '+') return false

    const currentSkills = Object.keys(this.getQuestion().skills)
    const skills = this.getSkills()
    const suggestSkills = skills.filter((skill) => currentSkills.indexOf(skill.name) == -1)
    if (suggestSkills.length == 0) return true

    callback(
      null,
      suggestSkills.map((skill) => {
        return { score: 1, meta: "", name: skill.name, value: `+ ${skill.name}` }
      })
    )
    return true
  }

  private suggestInputingSkill(
    prefix: string,
    session: AceEditor.IEditSession,
    pos: IPosition,
    callback: ICompletionCallback
  ): boolean {
    const inputingSkill = this.inputingSkill(session, pos)
    if (inputingSkill === null) return false

    const currentSkills = Object.keys(this.getQuestion().skills)
    const skills = this.getSkills()
    const suggestSkills = skills.filter(
      (skill) => currentSkills.indexOf(skill.name) == -1 && skill.name.indexOf(inputingSkill) == 0
    )
    if (suggestSkills.length == 0) return true

    callback(
      null,
      suggestSkills.map((skill) => {
        return { score: 1, meta: "", name: skill.name, value: `${skill.name}` }
      })
    )
    return true
  }

  private suggestSkillItem(
    prefix: string,
    session: AceEditor.IEditSession,
    pos: IPosition,
    callback: ICompletionCallback
  ): boolean {
    if (!this.inExplanation(session, pos)) return false

    const currentSkill = this.getCurrentSkill(session, pos)
    if (!currentSkill) return false

    if (this.suggestAllSkillItem(currentSkill, prefix, session, pos, callback)) return true
    if (this.suggestInputingSkillItem(currentSkill, prefix, session, pos, callback)) return true
    if (this.suggestInputingSkillItemAnswer(currentSkill, prefix, session, pos, callback)) return true

    return false
  }

  private suggestAllSkillItem(
    currentSkill: Skill,
    prefix: string,
    session: AceEditor.IEditSession,
    pos: IPosition,
    callback: ICompletionCallback
  ): boolean {
    if ((prefix != '-') && (prefix != '  - ')) return false

    const currentSkillItems = this.getQuestion().skills[currentSkill.name] || []
    const suggestSkillItems = currentSkill.skillItems.filter(
      (skillItem) => currentSkillItems.findIndex((item) => item.name == skillItem.name) == -1
    )

    if (suggestSkillItems.length == 0) return true
    callback(
      null,
      suggestSkillItems.map((skillItem) => {
        let value = `${prefix}${skillItem.name}`
        // skip prompt_ref(id: null)
        if (this.getQuestion().isTextarea && skillItem.id) {
          if (currentSkill.name == "addition_from_ai") {
            value = value += "\n  --"
          } else {
            value = `${value} ${this.getQuestion().textAreaAnswerRaw}`
          }
        }
        return {
          score: 1,
          meta: "",
          name: skillItem.name,
          value: value
        }
      })
    )
    return true
  }

  private suggestInputingSkillItem(
    currentSkill: Skill,
    prefix: string,
    session: AceEditor.IEditSession,
    pos: IPosition,
    callback: ICompletionCallback
  ): boolean {
    const inputingSkillItem = this.inputingSkillItem(session, pos)
    if (inputingSkillItem === null) return false

    const currentSkillItems = this.getQuestion().skills[currentSkill.name] || []

    const suggestSkillItems = currentSkill.skillItems.filter(
      (skillItem) =>
        currentSkillItems.findIndex((item) => item.name == skillItem.name) == -1 &&
        skillItem.name.indexOf(inputingSkillItem) == 0
    )
    if (suggestSkillItems.length == 0) return false

    callback(
      null,
      suggestSkillItems.map((skillItem) => {
        let value = `${skillItem.name}`
        if (this.getQuestion().isTextarea) {
          value = `${value} ${this.getQuestion().textAreaAnswerRaw}`
        }
        return {
          score: 1,
          meta: skillItem.name,
          name: skillItem.name,
          value: value
        }
      })
    )
    return true
  }

  private suggestInputingSkillItemAnswer(
    currentSkill: Skill,
    prefix: string,
    session: AceEditor.IEditSession,
    pos: IPosition,
    callback: ICompletionCallback
  ): boolean {
    if (prefix != '$') return false
    callback(null, [
      {
        score: 1,
        meta: this.getQuestion().textAreaAnswerRaw,
        name: this.getQuestion().textAreaAnswerRaw,
        value: this.getQuestion().textAreaAnswerRaw
      }
    ])

    return false
  }

  private suggestionForQuestion(question: Question): IAutoComplete[] {
    return question.type.nextSuggest(question)
  }

  private suggestionForAllQuestion(question: Question): IAutoComplete[] {
    let completionValues: IAutoComplete[] = []
    const questionTypes = this.getQuestionTypes()
    questionTypes.forEach((questionType) => {
      const completion = questionType.nextSuggest(question)
      completionValues = completionValues.concat(completion)
    })
    return completionValues
  }

  private inExplanation(session: AceEditor.IEditSession, pos: IPosition): boolean {
    const body = session.getValue()
    const lines = body.split('\n')
    for (let line = pos.row; line >= 0; line--) {
      const lineBody = lines[line] || ''
      if (lineBody.match(/^~~~+/)) return true
      else if (lineBody.match(/^(===+)|(---+)/)) return false
    }
    return false
  }

  private getCurrentSkill(session: AceEditor.IEditSession, pos: IPosition): Skill {
    const body = session.getValue()
    const lines = body.split('\n')
    for (let line = pos.row; line >= 0; line--) {
      const lineBody = lines[line] || ''
      if (lineBody.match(/^(===+)|(---+)|(~~~+)/)) return null
      let match
      if (!(match = lineBody.match(/^\s*\+\s*(.+)$/))) continue

      const skillName = match[1]
      const skill = this.getSkills().find((skill) => skill.name == skillName)
      return skill
    }
    return null
  }

  private inputingSkill(session: AceEditor.IEditSession, pos: IPosition): string {
    const body = session.getValue()
    const lines = body.split('\n')
    let match
    if (!(match = lines[pos.row].match(/^\s*\+\s*(.*)$/))) return null
    return match[1] || ''
  }

  private inputingSkillItem(session: AceEditor.IEditSession, pos: IPosition): string {
    const body = session.getValue()
    const lines = body.split('\n')
    let match
    if (!(match = lines[pos.row].match(/^\s*\-\s*(.*)$/))) return null
    return match[1] || ''
  }
}
